import {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Button,
    ButtonBase,
    Dialog,
    DialogTitle,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";

import { Add, Close, DeleteOutline, Info } from "@mui/icons-material";

import {
    DataGrid,
    GridActionsCellItem,
    GridColDef,
    GridRenderCellParams,
    GridRowId,
    GridRowSelectionModel,
    GridToolbar,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";

import { EmptyRecords, StatusChip } from "../../../components/common";
import { useGetAllProducts } from "../../../sdk/hooks";
import { formatCurrency } from "../../../sdk/utils/numbers";
import { globalDateTimeFormat } from "../../../utils/constants";
import { productStatusByEnum } from "../../../utils/enums";

const Root = styled("section")`
    padding: ${({ theme }) => theme.spacing(1, 2)};
    max-width: 100%;
    min-height: 500px;

    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }

    .MuiDataGrid-row:hover {
        cursor: pointer;
    }
`;
const Header = styled(DialogTitle)`
    width: 100%;
    background-color: #f0f0f0;

    display: flex;
    justify-content: space-between;
    align-items: start;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled(DialogTitle)``;

const FormHeading = styled(Typography)`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};

    font-size: 20px;
    font-weight: 500;
`;

const FormCloseHandler = styled("div")`
    display: flex;
    justify-content: center;
`;

const FormDrawerCloseButton = styled(ButtonBase)`
    width: 32px;
    height: 32px;
    border-radius: 50%;
`;

const ButtonContainer = styled("div")`
    width: 100%;
    display: flex;
    justify-content: end;

    padding-top: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};

    width: 100%;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
          flex-direction: column;
    }
  `}
`;

const InfoText = styled(Typography)`
    display: flex;
    width: 100%;
`;

const columns: GridColDef[] = [
    {
        field: "productName",
        headerName: "Product",
        flex: 0.5,
        valueGetter: (params: GridValueGetterParams) => params.row.productTitle,
    },
    {
        field: "name",
        headerName: "Variant",
        flex: 0.5,
        valueGetter: (params: GridValueGetterParams) => params.row.title,
    },
    {
        field: "mrp",
        headerName: "MRP",
        flex: 0.3,
        valueGetter: (params: GridValueGetterParams) =>
            formatCurrency(params.row.mrp),
    },
    {
        field: "price",
        headerName: "Selling price",
        flex: 0.4,
        valueGetter: (params: GridValueGetterParams) =>
            formatCurrency(params.row.price),
    },
    {
        field: "productStatus",
        headerName: "Product status",
        flex: 0.4,
        renderCell: (params: GridRenderCellParams<Date>) => (
            <StatusChip
                label={productStatusByEnum[params.value]}
                value={params.value}
            />
        ),
    },
    {
        field: "status",
        headerName: "Variant status",
        flex: 0.4,
        renderCell: (params: GridRenderCellParams<Date>) => (
            <StatusChip
                label={productStatusByEnum[params.value]}
                value={params.value}
            />
        ),
    },
    {
        field: "createdAt",
        headerName: "Created at",
        flex: 0.5,
        valueGetter: (params: GridValueGetterParams) =>
            `${format(new Date(params.row.createdAt), globalDateTimeFormat)}`,
    },
];

const selectedVariantsColumns: GridColDef[] = [
    {
        field: "productName",
        headerName: "Product",
        flex: 0.5,
        valueGetter: (params: GridValueGetterParams) => params.row.productTitle,
    },
    {
        field: "name",
        headerName: "Variant",
        flex: 0.5,
        valueGetter: (params: GridValueGetterParams) => params.row.title,
    },
    {
        field: "mrp",
        headerName: "MRP",
        flex: 0.3,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.mrp === null
                ? "—"
                : formatCurrency(params.row.mrp),
    },
    {
        field: "price",
        headerName: "Selling price",
        flex: 0.4,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.price === null
                ? "—"
                : formatCurrency(params.row.price),
    },
];

export interface IFreebieVariantsProps {
    freebieVariantIds: string[];
    onChange: (newValue: GridRowId[] | undefined) => void;
}

export const FreebieVariants: FunctionComponent<IFreebieVariantsProps> = (
    props: IFreebieVariantsProps,
): ReactElement => {
    const { freebieVariantIds, onChange } = props;
    const [open, setOpen] = useState(false);

    const handleToggleDialog = useCallback(() => {
        setOpen((oldOpen) => !oldOpen);
    }, []);

    /*
     * Every status, so this list matches what `/products` shows. It used to
     * ask for PUBLISHED only, which silently hid draft and archived products
     * an admin could see on the products page and expected to pick here. The
     * Product status column shows what each row actually is.
     */
    const fetchProductsQuery = useGetAllProducts(["products", "all-statuses"]);

    const products = useMemo(() => {
        const productList = fetchProductsQuery.data ?? [];
        return productList;
    }, [fetchProductsQuery.data]);

    const productVariants = useMemo(
        () =>
            products.flatMap((product) =>
                product.variants.map((variant) => ({
                    ...variant,
                    productTitle: product.title,
                    productStatus: product.status,
                })),
            ),
        [products],
    );

    /*
     * Variants that are no longer published are not returned by the products
     * query, but may still be attached to the rule. They are rendered as
     * placeholder rows so they can be seen and removed.
     */
    const selectedVariants = useMemo(() => {
        const variantsById = new Map(
            productVariants.map((variant) => [variant.id, variant]),
        );
        return freebieVariantIds.map(
            (id) =>
                variantsById.get(id) ?? {
                    id,
                    productTitle: "Unavailable product",
                    title: id,
                    mrp: null,
                    price: null,
                },
        );
    }, [productVariants, freebieVariantIds]);

    const handleRemoveVariant = useCallback(
        (variantId: GridRowId) => () => {
            onChange(freebieVariantIds.filter((id) => id !== variantId));
        },
        [onChange, freebieVariantIds],
    );

    const selectedColumns: GridColDef[] = useMemo(
        () => [
            ...selectedVariantsColumns,
            {
                field: "actions",
                headerName: "Actions",
                type: "actions",
                width: 80,
                getActions: (params) => [
                    <GridActionsCellItem
                        key="remove"
                        icon={<DeleteOutline fontSize="small" />}
                        label="Remove"
                        onClick={handleRemoveVariant(params.id)}
                        color="inherit"
                    />,
                ],
            },
        ],
        [handleRemoveVariant],
    );

    const [selectedProductVariantIds, setSelectedProductVariantIds] =
        useState<GridRowId[]>();

    useEffect(() => {
        setSelectedProductVariantIds(freebieVariantIds);
    }, [freebieVariantIds]);

    const handleCancel = useCallback(() => {
        /* Undo the changes made */
        setSelectedProductVariantIds(freebieVariantIds);

        /* Close the dialog */
        handleToggleDialog();
    }, [handleToggleDialog, freebieVariantIds]);

    const handleSubmit = useCallback(() => {
        onChange(selectedProductVariantIds);
        handleToggleDialog();
    }, [handleToggleDialog, onChange, selectedProductVariantIds]);

    const handleRowSelection = useCallback(
        (newSelection: GridRowSelectionModel) => {
            setSelectedProductVariantIds(newSelection as string[]);
        },
        [],
    );

    return (
        <>
            <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="lg">
                <Header>
                    <Title>Freebie Variants</Title>
                    <FormHeading>
                        Select product variants
                        <Tooltip
                            enterTouchDelay={0}
                            title="Specifies the product variant(s) granted for free when this rule triggers"
                        >
                            <Info />
                        </Tooltip>
                    </FormHeading>
                    <FormCloseHandler>
                        <FormDrawerCloseButton onClick={handleCancel}>
                            <Close />
                        </FormDrawerCloseButton>
                    </FormCloseHandler>
                </Header>
                <Root>
                    {fetchProductsQuery.data?.length === 0 && (
                        <EmptyRecords>
                            <EmptyRecordsContainer>
                                <Title>No product variants available</Title>
                                <div>
                                    Create at least one variant associated with
                                    a product use this feature
                                </div>
                            </EmptyRecordsContainer>
                        </EmptyRecords>
                    )}
                    {fetchProductsQuery.isSuccess &&
                        fetchProductsQuery.data?.length > 0 && (
                            <StyledDataGrid
                                rows={productVariants ?? []}
                                columns={columns}
                                rowSelection={true}
                                rowSelectionModel={selectedProductVariantIds}
                                checkboxSelection={true}
                                keepNonExistentRowsSelected={true}
                                onRowSelectionModelChange={handleRowSelection}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                pageSizeOptions={[10]}
                                loading={fetchProductsQuery.isLoading}
                                slots={{ toolbar: GridToolbar }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        csvOptions: {
                                            disableToolbarButton: true,
                                        },
                                        printOptions: {
                                            disableToolbarButton: true,
                                        },
                                    },
                                }}
                            />
                        )}
                    <ButtonContainer>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </ButtonContainer>
                </Root>
            </Dialog>
            {freebieVariantIds.length === 0 && (
                <Row>
                    <InfoText>No product variants selected</InfoText>
                    <ButtonContainer>
                        <Button
                            variant="outlined"
                            onClick={handleToggleDialog}
                            startIcon={<Add fontSize="small" />}
                        >
                            Add product variants
                        </Button>
                    </ButtonContainer>
                </Row>
            )}
            {freebieVariantIds.length > 0 && (
                <>
                    <StyledDataGrid
                        rows={selectedVariants}
                        columns={selectedColumns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                        }}
                        pageSizeOptions={[10]}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                csvOptions: {
                                    disableToolbarButton: true,
                                },
                                printOptions: {
                                    disableToolbarButton: true,
                                },
                            },
                        }}
                    />
                    <Row>
                        <InfoText>
                            {`${freebieVariantIds.length} product variants selected`}
                        </InfoText>
                        <ButtonContainer>
                            <Button
                                variant="outlined"
                                onClick={handleToggleDialog}
                            >
                                Manage selection
                            </Button>
                        </ButtonContainer>
                    </Row>
                </>
            )}
        </>
    );
};
